import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Database,
  HardDrive,
  Zap,
  Layers,
  BarChart2,
  Search,
  Loader2,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Cpu,
  Clock,
  Activity,
  FileText,
  RefreshCw,
  Server,
} from 'lucide-react';
import baseapi from '../../../services/baseapi';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import { DatabaseDetailsPanel } from './DatabaseDetailsPanel';
import { DBExportModal } from './DBExportModal';

export type SupportedEngine = 'clickhouse' | 'postgresql' | 'mariadb' | 'mysql' | 'mongodb' | 'mssql';

interface OverviewViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

interface DatabaseItem {
  database: string;
  category: 'System' | 'Application';
  total_size: number | string;
  db_type?: string;
  capacity_status?: string | null;
  backup_flags?: string[] | null;
  last_seen?: string | null;
  size_source?: string | null;
  raw?: Record<string, any>;
  [key: string]: any;

  // MongoDB specific
  collection?: string;
  db_name?: string;
  docs_count?: number | null;
  storage_size_mb?: number | null;
  index_size_mb?: number | null;
  index_count?: number | null;
  avg_obj_size_b?: number | null;
  is_capped?: boolean | null;

  // PostgreSQL specific
  connections?: number | null;
  open_txns?: number | null;
  cache_hit_pct?: number | null;
  live_tuples?: number | null;
  dead_tuples?: number | null;
  dead_tuples_pct?: number | null;
  xid_age?: number | null;
  commits_per_s?: number | null;
  rollbacks_per_s?: number | null;
  blks_read_per_s?: number | null;
  blks_hit_per_s?: number | null;

  // ClickHouse specific
  compressed_size?: number | string | null;
  uncompressed_data_size?: number | string | null;
  active_parts?: number | string | null;
  active_merges?: number | string | null;
  pending_mutations?: number | string | null;
  failed_mutations?: number | string | null;
  engine?: string | null;

  // MySQL / MariaDB specific
  data_size_mb?: number | null;
  table_count?: number | null;
  view_count?: number | null;
  system_view_count?: number | null;
  row_estimate?: number | null;
  active_connections?: number | null;
  max_txn_time_sec?: number | null;

  // MSSQL specific
  data_file_used_mb?: number | null;
  log_file_used_mb?: number | null;
  status?: string | null;
  recovery_model?: string | null;
  active_transactions?: number | null;
  transactions_per_s?: number | null;
  write_transactions_per_s?: number | null;

  // Common fallbacks
  tables?: number | string | null;
  views?: number | string | null;
}

type SortKey = Extract<keyof DatabaseItem, string>;

const parseSizeToMB = (val: any): number => {
  if (val === null || val === undefined || val === '' || val === '—' || val === '-') return 0;
  if (typeof val === 'number') {
    if (val >= 100000) return val / (1024 * 1024);
    return val;
  }
  if (typeof val === 'string') {
    const s = val.trim().toUpperCase();
    const num = parseFloat(s.replace(/[^0-9.]/g, ''));
    if (isNaN(num)) return 0;
    if (s.includes('TB')) return num * 1024 * 1024;
    if (s.includes('GB')) return num * 1024;
    if (s.includes('MB')) return num;
    if (s.includes('KB')) return num / 1024;
    if (s.includes('B')) return num / (1024 * 1024);
    return num;
  }
  return 0;
};

const formatSize = (val: any): string => {
  if (val === null || val === undefined || val === '' || val === '-' || val === '—') return '—';
  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (/[a-zA-Z]/.test(trimmed)) return trimmed;
    const num = parseFloat(trimmed);
    if (!isNaN(num)) val = num;
    else return trimmed;
  }
  if (typeof val === 'number') {
    if (val === 0) return '0.00 MB';
    if (val >= 100000) {
      if (val >= 1024 * 1024 * 1024) return `${(val / (1024 * 1024 * 1024)).toFixed(2)} GB`;
      if (val >= 1024 * 1024) return `${(val / (1024 * 1024)).toFixed(2)} MB`;
      if (val >= 1024) return `${(val / (1024 * 1024)).toFixed(2)} KB`;
      return `${val.toFixed(2)} B`;
    }
    if (val >= 1024) return `${(val / 1024).toFixed(2)} GB`;
    if (val >= 0.001) return `${val.toFixed(2)} MB`;
    return '0.00 MB';
  }
  return String(val);
};


// 50 vibrant, distinguishable colors for database size distribution
const DISTRIBUTION_COLORS = [
  '#3b82f6', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899',
  '#06b6d4', '#f97316', '#14b8a6', '#a855f7', '#ef4444',
  '#6366f1', '#84cc16', '#d946ef', '#0ea5e9', '#eab308',
  '#22c55e', '#f43f5e', '#64748b', '#2dd4bf', '#38bdf8',
  '#818cf8', '#c084fc', '#e879f9', '#fb7185', '#f87171',
  '#fb923c', '#fbbf24', '#a3e635', '#4ade80', '#34d399',
  '#22d3ee', '#60a5fa', '#9333ea', '#c026d3', '#db2777',
  '#e11d48', '#ea580c', '#d97706', '#ca8a04', '#65a30d',
  '#16a34a', '#059669', '#0d9488', '#0891b2', '#0284c7',
  '#2563eb', '#4f46e5', '#7c3aed', '#94a3b8', '#f472b6',
];


export const OverviewView: React.FC<OverviewViewProps> = ({
  activeCluster,
  clientId,
  refreshKey,
  dbType,
}) => {
  const [loading, setLoading] = useState(true);
  const [overviewData, setOverviewData] = useState<any>(null);
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'application' | 'system'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('total_size');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [selectedDb, setSelectedDb] = useState<DatabaseItem | null>(null);
  const [hoveredDbIndex, setHoveredDbIndex] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const safeGet = async (url: string, params: any) => {
      try {
        const res = await baseapi.get(url, { params });
        // Return full Axios response body ({ status, data, summary, total_size_mb, total_count })
        return res.data;
      } catch (e) {
        return null;
      }
    };

    try {
      // Single unified API call for all databases
      const perDbRes = await safeGet('/db_monitor_dashboard/per_db/overview', {
        cluster_id: activeCluster,
        client_id: clientId,
      });
      setOverviewData(perDbRes);
    } catch (err) {
      console.warn('OverviewView fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  useEffect(() => {
    setCurrentPage(1);
  }, [categoryFilter, searchQuery]);

  // Extract raw database list
  const rawDatabases = useMemo(() => {
    if (!overviewData) return [];
    if (Array.isArray(overviewData?.data) && overviewData.data.length > 0) {
      return overviewData.data;
    }
    if (Array.isArray(overviewData?.databases) && overviewData.databases.length > 0) {
      return overviewData.databases;
    }
    if (Array.isArray(overviewData?.data?.databases) && overviewData.data.databases.length > 0) {
      return overviewData.data.databases;
    }
    if (Array.isArray(overviewData) && overviewData.length > 0) {
      return overviewData;
    }
    return [];
  }, [overviewData]);

  // Detect DB engine dynamically
  const detectedEngine: SupportedEngine = useMemo(() => {
    const fromProp = (dbType || '').toLowerCase();
    if (fromProp.includes('clickhouse')) return 'clickhouse';
    if (fromProp.includes('postgres')) return 'postgresql';
    if (fromProp.includes('mongo')) return 'mongodb';
    if (fromProp.includes('mssql') || fromProp.includes('sqlserver')) return 'mssql';
    if (fromProp.includes('maria')) return 'mariadb';
    if (fromProp.includes('mysql')) return 'mysql';

    const firstType = (rawDatabases[0]?.db_type || '').toLowerCase();
    if (firstType.includes('clickhouse')) return 'clickhouse';
    if (firstType.includes('postgres')) return 'postgresql';
    if (firstType.includes('mongo')) return 'mongodb';
    if (firstType.includes('mssql') || firstType.includes('sqlserver')) return 'mssql';
    if (firstType.includes('maria')) return 'mariadb';
    if (firstType.includes('mysql')) return 'mysql';

    return 'clickhouse';
  }, [dbType, rawDatabases]);

  // Normalize database rows
  const normalizedDatabases: DatabaseItem[] = useMemo(() => {
    return rawDatabases.map((item: any) => {
      const name = item.database || item.database_name || item.name || item.db_name || '';
      const lowerName = name.toLowerCase();
      const isSys =
        item.is_system === true ||
        (item.category && String(item.category).toLowerCase() === 'system') ||
        ['system', 'information_schema', 'performance_schema', 'mysql', 'sys', 'local', 'master', 'msdb', 'model', 'tempdb'].includes(lowerName);

      const category = isSys ? 'System' : 'Application';

      return {
        ...item,
        raw: item,
        database: name,
        category,
        total_size: item.size_mb ?? item.total_size_mb ?? item.total_size ?? item.size ?? 0,
        db_type: item.db_type,
        capacity_status: item.capacity_status,
        backup_flags: item.backup_flags,
        last_seen: item.last_seen,
        size_source: item.size_source,

        // MongoDB
        collection: item.collection,
        db_name: item.db_name,
        docs_count: item.docs_count,
        storage_size_mb: item.storage_size_mb,
        index_size_mb: item.index_size_mb,
        index_count: item.index_count,
        avg_obj_size_b: item.avg_obj_size_b,
        is_capped: item.is_capped,

        // PostgreSQL
        connections: item.connections,
        open_txns: item.open_txns,
        cache_hit_pct: item.cache_hit_pct,
        live_tuples: item.live_tuples,
        dead_tuples: item.dead_tuples,
        dead_tuples_pct: item.dead_tuples_pct,
        xid_age: item.xid_age,
        commits_per_s: item.commits_per_s,
        rollbacks_per_s: item.rollbacks_per_s,
        blks_read_per_s: item.blks_read_per_s,
        blks_hit_per_s: item.blks_hit_per_s,

        // ClickHouse
        compressed_size: item.compressed_mb ?? item.compressed_size ?? item.compressed_size_on_disk ?? null,
        uncompressed_data_size: item.uncompressed_mb ?? item.uncompressed_data_size ?? item.uncompressed_size ?? null,
        active_parts: item.active_parts ?? null,
        active_merges: item.active_merges ?? 0,
        pending_mutations: item.pending_mutations ?? 0,
        failed_mutations: item.failed_mutations ?? 0,
        engine: item.engine,

        // MySQL / MariaDB
        data_size_mb: item.data_size_mb,
        table_count: item.table_count,
        view_count: item.view_count,
        system_view_count: item.system_view_count,
        row_estimate: item.row_estimate,
        active_connections: item.active_connections,
        max_txn_time_sec: item.max_txn_time_sec,

        // MSSQL
        data_file_used_mb: item.data_file_used_mb,
        log_file_used_mb: item.log_file_used_mb,
        status: item.status,
        recovery_model: item.recovery_model,
        active_transactions: item.active_transactions,
        transactions_per_s: item.transactions_per_s,
        write_transactions_per_s: item.write_transactions_per_s,

        // Common
        tables: item.table_count ?? item.tables ?? item.tables_count ?? 0,
        views: item.view_count ?? item.views ?? item.views_count ?? 0,
      };
    });
  }, [rawDatabases]);

  // Unified Summary Statistics
  const summary = useMemo(() => {
    const sumData = overviewData?.summary || overviewData?.data?.summary || {};
    const dbs = normalizedDatabases;
    const instance = sumData.instance || {};

    const totalDatabases = sumData.total_databases ?? overviewData?.total_count ?? dbs.length;
    const appDatabases = sumData.application_databases ?? dbs.filter((d) => d.category === 'Application').length;
    const sysDatabases = sumData.system_databases ?? dbs.filter((d) => d.category === 'System').length;

    const totalSizeMB = sumData.total_size_mb ?? overviewData?.total_size_mb ?? dbs.reduce((acc, d) => acc + parseSizeToMB(d.total_size), 0);
    const totalDatabaseSize = formatSize(totalSizeMB);

    const appSizeMB = sumData.application_size_mb ?? dbs.filter((d) => d.category === 'Application').reduce((acc, d) => acc + parseSizeToMB(d.total_size), 0);
    const appSize = formatSize(appSizeMB);

    const activeConns = instance.connections_active ?? sumData.active_connections ?? sumData.connections ?? instance.connections_total ?? 0;
    const maxConns = instance.connections_max ?? null;

    const activeMerges = sumData.active_merges ?? dbs.reduce((acc, d) => acc + (Number(d.active_merges) || 0), 0);
    const pendingMutations = sumData.pending_mutations ?? dbs.reduce((acc, d) => acc + (Number(d.pending_mutations) || 0), 0);
    const replicatedTables = sumData.replicated_tables ?? null;
    const totalDocs = dbs.reduce((acc, d) => acc + (Number(d.docs_count) || 0), 0);

    return {
      totalDatabases,
      appDatabases,
      sysDatabases,
      totalDatabaseSize,
      appSize,
      activeConns,
      maxConns,
      activeMerges,
      pendingMutations,
      replicatedTables,
      totalDocs,
      instance,
      rawSummary: sumData,
    };
  }, [overviewData, normalizedDatabases]);

  // Handle table column sort
  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
  };

  // Filter and sort databases
  const filteredDatabases = useMemo(() => {
    const list = normalizedDatabases.filter((d) => {
      const matchCategory =
        categoryFilter === 'all' ||
        (categoryFilter === 'application' && d.category === 'Application') ||
        (categoryFilter === 'system' && d.category === 'System');

      const matchSearch =
        !searchQuery.trim() ||
        d.database.toLowerCase().includes(searchQuery.toLowerCase().trim()) ||
        (d.collection && d.collection.toLowerCase().includes(searchQuery.toLowerCase().trim()));

      return matchCategory && matchSearch;
    });

    list.sort((a: any, b: any) => {
      let valA = a[sortKey];
      let valB = b[sortKey];

      if (['total_size', 'compressed_size', 'uncompressed_data_size', 'storage_size_mb', 'index_size_mb', 'data_size_mb', 'data_file_used_mb', 'log_file_used_mb'].includes(sortKey)) {
        valA = parseSizeToMB(valA);
        valB = parseSizeToMB(valB);
      } else if (typeof valA === 'string') {
        valA = valA.toLowerCase();
        valB = (valB || '').toString().toLowerCase();
      } else {
        valA = Number(valA) || 0;
        valB = Number(valB) || 0;
      }

      if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
      if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return list;
  }, [normalizedDatabases, categoryFilter, searchQuery, sortKey, sortDirection]);

  // Pagination calculations
  const totalItems = filteredDatabases.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  const paginatedDatabases = useMemo(() => {
    return filteredDatabases.slice(startIndex, endIndex);
  }, [filteredDatabases, startIndex, endIndex]);

  // Database size distribution (sorted descending by total size, top 3 + Others)
  const sortedDbsBySize = useMemo(() => {
    const list = [...normalizedDatabases].map((d) => ({
      name: d.database,
      sizeMB: parseSizeToMB(d.total_size),
      formattedSize: formatSize(d.total_size),
      rawDb: d,
    }));
    list.sort((a, b) => b.sizeMB - a.sizeMB);
    const totalSizeMB = list.reduce((acc, d) => acc + (d.sizeMB || 0), 0);
    const mapped = list.map((item) => ({
      ...item,
      percentage: totalSizeMB > 0 ? (item.sizeMB / totalSizeMB) * 100 : 0,
      totalSizeMB,
      isOthers: false,
      otherNames: '',
    }));

    if (mapped.length <= 3) {
      return mapped;
    }

    const top3 = mapped.slice(0, 3);
    const others = mapped.slice(3);
    const othersSizeMB = others.reduce((acc, d) => acc + (d.sizeMB || 0), 0);
    const othersPercentage = others.reduce((acc, d) => acc + (d.percentage || 0), 0);

    const othersItem = {
      name: 'Others',
      sizeMB: othersSizeMB,
      formattedSize: formatSize(othersSizeMB),
      rawDb: null,
      percentage: othersPercentage,
      totalSizeMB,
      isOthers: true,
      otherNames: others.map((o) => `${o.name} (${o.formattedSize})`).join(', '),
    };

    return [...top3, othersItem];
  }, [normalizedDatabases]);

  const totalDbsSizeFormatted = useMemo(() => {
    if (!sortedDbsBySize.length) return '0 MB';
    const totalMB = sortedDbsBySize[0]?.totalSizeMB || 0;
    return formatSize(totalMB);
  }, [sortedDbsBySize]);

  const isMongo = detectedEngine === 'mongodb';

  // Dynamic columns shown in the UI for the active engine
  const inventoryExportColumns = useMemo(() => {
    // Base common columns shown for all engines
    const cols: { key: string; label: string }[] = [
      { key: 'database', label: isMongo ? 'Collection / DB' : 'Database' },
      { key: 'category', label: 'Category' },
      { key: 'total_size', label: 'Total Size' },
    ];

    if (detectedEngine === 'clickhouse') {
      cols.push(
        { key: 'compressed_size', label: 'Compressed (Disk)' },
        { key: 'uncompressed_data_size', label: 'Uncompressed Data' },
        { key: 'active_parts', label: 'Parts' },
        { key: 'active_merges', label: 'Merges' },
        { key: 'pending_mutations', label: 'Mutations' },
        { key: 'tables', label: 'Tables' },
        { key: 'views', label: 'Views' }
      );
    } else if (detectedEngine === 'postgresql') {
      cols.push(
        { key: 'connections', label: 'Connections' },
        { key: 'open_txns', label: 'Open Txns' },
        { key: 'cache_hit_pct', label: 'Cache Hit %' },
        { key: 'commits_per_s', label: 'Commits/s' },
        { key: 'rollbacks_per_s', label: 'Rollbacks/s' },
        { key: 'dead_tuples_pct', label: 'Dead Tuples %' }
      );
    } else if (detectedEngine === 'mysql' || detectedEngine === 'mariadb') {
      cols.push(
        { key: 'data_size_mb', label: 'Data Size' },
        { key: 'index_size_mb', label: 'Index Size' },
        { key: 'table_count', label: 'Tables' },
        { key: 'view_count', label: 'Views' },
        { key: 'row_estimate', label: 'Est. Rows' },
        { key: 'active_connections', label: 'Connections' }
      );
    } else if (detectedEngine === 'mongodb') {
      cols.push(
        { key: 'storage_size_mb', label: 'Storage Size' },
        { key: 'index_size_mb', label: 'Index Size' },
        { key: 'docs_count', label: 'Documents' },
        { key: 'avg_obj_size_b', label: 'Avg Obj Size' },
        { key: 'index_count', label: 'Indexes' },
        { key: 'is_capped', label: 'Capped' }
      );
    } else if (detectedEngine === 'mssql') {
      cols.push(
        { key: 'data_file_used_mb', label: 'Data File Used' },
        { key: 'log_file_used_mb', label: 'Log File Used' },
        { key: 'recovery_model', label: 'Recovery Model' },
        { key: 'transactions_per_s', label: 'Txns/s' },
        { key: 'status', label: 'State' }
      );
    }

    return cols;
  }, [detectedEngine, isMongo]);

  // Dynamic list of metric cards: renders exclusively from summary.cards
  const metricCards = useMemo(() => {
    const sumData = overviewData?.summary || overviewData?.data?.summary || {};

    const getIcon = (key: string, color?: string) => {
      const k = key.toLowerCase();
      const style = color ? { color } : undefined;
      const cls = color ? "w-3.5 h-3.5" : "w-3.5 h-3.5 text-orange-500";
      if (k.includes('database') || k.includes('collection')) return <Database className={cls} style={style} />;
      if (k.includes('application')) return <Layers className={cls} style={style} />;
      if (k.includes('system')) return <Database className={cls} style={style} />;
      if (k.includes('storage') || k.includes('table')) return <Layers className={cls} style={style} />;
      if (k.includes('size')) return <HardDrive className={cls} style={style} />;
      if (k.includes('connection')) return <Zap className={cls} style={style} />;
      if (k.includes('memory') || k.includes('ram')) return <Cpu className={cls} style={style} />;
      if (k.includes('quer') || k.includes('txn') || k.includes('commit') || k.includes('hit') || k.includes('cache')) return <Activity className={cls} style={style} />;
      if (k.includes('merge') || k.includes('mutation') || k.includes('replicated')) return <RefreshCw className={cls} style={style} />;
      if (k.includes('uptime') || k.includes('time') || k.includes('sec')) return <Clock className={cls} style={style} />;
      if (k.includes('version')) return <Server className={cls} style={style} />;
      if (k.includes('doc') || k.includes('view') || k.includes('row')) return <FileText className={cls} style={style} />;
      return <Activity className={cls} style={style} />;
    };

    const apiCards = sumData?.cards;
    if (Array.isArray(apiCards)) {
      return apiCards.map((c: any, index: number) => {
        const color = DISTRIBUTION_COLORS[index % DISTRIBUTION_COLORS.length];
        return {
          key: c.key || `card_${index}`,
          title: c.label || c.key || 'Metric',
          value: c.data !== undefined && c.data !== null ? String(c.data) : '—',
          subData: Array.isArray(c.sub_data) ? c.sub_data : [],
          icon: getIcon(c.key || c.label || '', color),
          color,
        };
      });
    }

    return [];
  }, [overviewData]);



  if (loading && !overviewData) {
    return (
      <div className="flex items-center justify-center rounded-xl min-h-[350px]">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-3">
      {/* Dynamic Summary Metric Cards (Full-Width Responsive Auto-Fit Grid) */}
      <div
        className="grid gap-1 sm:gap-1"
        style={{
          gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, 220px), 1fr))`,
        }}
      >
        {metricCards.map((card) => {
          const cardColor = card.color || '#f97316';
          return (
            <div
              key={card.key}
              className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-2.5 sm:p-3 shadow-sm transition-colors duration-200 flex flex-col justify-between min-w-0"
            >
              <div>
                <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider mb-1 gap-1">
                  <span className="truncate" style={{ color: cardColor }} title={card.title}>
                    {card.title}
                  </span>
                  <span className="shrink-0" style={{ color: cardColor }}>
                    {card.icon}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1 min-w-0">
                  <div
                    className={`text-base sm:text-lg font-bold font-mono truncate ${card.subData && card.subData.length > 0 ? 'max-w-[48%] min-w-[30%] shrink-0' : 'w-full'
                      }`}
                    style={{ color: cardColor }}
                    title={String(card.value)}
                  >
                    {card.value}
                  </div>

                  {card.subData && card.subData.length > 0 && (
                    <div
                      className={`grid ${card.subData.length > 2 ? 'grid-cols-1 gap-x-2' : 'grid-cols-1'
                        } gap-y-0.5 min-w-0 flex-1 pl-2 border-l border-gray-200 dark:border-gray-200/60`}
                    >
                      {card.subData.map((item: any, sIdx: number) => {
                        const valStr =
                          item.data !== undefined && item.data !== null
                            ? typeof item.data === 'object'
                              ? JSON.stringify(item.data)
                              : String(item.data)
                            : '—';
                        const labelStr = String(item.label || '');

                        return (
                          <div
                            key={sIdx}
                            className="flex items-center justify-between text-[10px] leading-tight min-w-0 gap-1"
                            title={labelStr ? `${labelStr}: ${valStr}` : valStr}
                          >
                            <span
                              className="text-gray-500 dark:text-gray-100 truncate shrink min-w-0"
                              title={labelStr}
                            >
                              {labelStr}
                            </span>
                            <span
                              className="text-gray-700 dark:text-gray-200 font-semibold font-mono truncate max-w-[60%] shrink-0 text-right"
                              title={valStr}
                            >
                              {valStr}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="w-full pt-1">
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-xl p-4 shadow-sm dark:shadow-xl transition-colors duration-200 space-y-3.5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-gray-200 dark:border-gray-700/60 pb-2.5">
            <div className="flex items-center gap-2">
              <div className="p-1.5 rounded-lg bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 border border-orange-200/50 dark:border-orange-500/20 shrink-0">
                <BarChart2 className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <h3 className="text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
                  {isMongo ? 'Collection Size Distribution' : 'Database Size Distribution'}
                </h3>
                <p className="text-[10px] text-gray-500 dark:text-gray-100">
                  Relative storage share across {isMongo ? 'collections' : 'databases'}
                </p>
              </div>
            </div>
            {sortedDbsBySize.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium px-2.5 py-1 rounded-lg bg-gray-100 dark:bg-gray-800/90 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700 font-mono">
                  Total: <strong className="text-gray-900 dark:text-white font-bold">{totalDbsSizeFormatted}</strong>
                  <span className="text-gray-100 dark:text-gray-500 ml-1.5">
                    ({normalizedDatabases.length} {normalizedDatabases.length === 1 ? (isMongo ? 'collection' : 'database') : (isMongo ? 'collections' : 'databases')})
                  </span>
                </span>
              </div>
            )}
          </div>

          {sortedDbsBySize.length === 0 ? (
            <div className="py-8 text-center text-xs text-gray-100">
              {isMongo ? 'No collection size data available' : 'No database size data available'}
            </div>
          ) : (
            <div className="space-y-2.5 pt-0.5">
              {/* Active / Hovered info strip */}
              <div className="flex items-center justify-between min-h-[22px] text-xs">
                {hoveredDbIndex !== null && sortedDbsBySize[hoveredDbIndex] ? (
                  <div className="flex items-center gap-2 text-xs font-mono">
                    <span
                      className="w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ backgroundColor: DISTRIBUTION_COLORS[hoveredDbIndex % DISTRIBUTION_COLORS.length] }}
                    />
                    <span className="font-bold text-gray-900 dark:text-white font-sans">
                      {sortedDbsBySize[hoveredDbIndex].name}
                    </span>
                    <span className="text-gray-100 dark:text-gray-500">•</span>
                    <span className="font-semibold text-gray-900 dark:text-white">
                      {sortedDbsBySize[hoveredDbIndex].formattedSize}
                    </span>
                    <span className="text-gray-500 dark:text-gray-100">
                      ({sortedDbsBySize[hoveredDbIndex].percentage.toFixed(1)}% of total)
                    </span>
                  </div>
                ) : (
                  <span className="text-[11px] text-gray-500 dark:text-gray-100">
                    Hover over segments or list below to inspect details
                  </span>
                )}
                <span className="text-[11px] text-gray-100 dark:text-gray-500 font-mono hidden sm:inline">
                  Total Storage
                </span>
              </div>

              {/* One Single Full Segregated Bar */}
              <div className="h-6 w-full rounded-lg overflow-hidden flex bg-gray-100 dark:bg-gray-900 p-0.5 border border-gray-200/80 dark:border-gray-700/60 shadow-inner">
                {sortedDbsBySize.map((db, idx) => {
                  const color = DISTRIBUTION_COLORS[idx % DISTRIBUTION_COLORS.length];
                  const isHovered = hoveredDbIndex === idx;
                  const isOtherHovered = hoveredDbIndex !== null && !isHovered;
                  return (
                    <div
                      key={idx}
                      onMouseEnter={() => setHoveredDbIndex(idx)}
                      onMouseLeave={() => setHoveredDbIndex(null)}
                      onClick={() => db.rawDb && setSelectedDb(db.rawDb)}
                      className={`h-full transition-all duration-150 cursor-pointer first:rounded-l-[5px] last:rounded-r-[5px] ${isOtherHovered ? 'opacity-30' : 'opacity-100 hover:brightness-110'
                        }`}
                      style={{
                        width: `${Math.max(db.percentage, 0.35)}%`,
                        backgroundColor: color,
                      }}
                      title={
                        db.otherNames
                          ? `Others: ${db.formattedSize} (${db.percentage.toFixed(1)}%) - ${db.otherNames}`
                          : `${db.name}: ${db.formattedSize} (${db.percentage.toFixed(1)}%)`
                      }
                    />
                  );
                })}
              </div>

              {/* Segregated Items Legend (Side-by-Side Flex Wrap) */}
              <div className="pt-2">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 max-h-72 overflow-y-auto pr-1">
                  {sortedDbsBySize.map((db, idx) => {
                    const color = DISTRIBUTION_COLORS[idx % DISTRIBUTION_COLORS.length];
                    const isHovered = hoveredDbIndex === idx;
                    return (
                      <div
                        key={idx}
                        onMouseEnter={() => setHoveredDbIndex(idx)}
                        onMouseLeave={() => setHoveredDbIndex(null)}
                        onClick={() => db.rawDb && setSelectedDb(db.rawDb)}
                        className={`flex items-center gap-1.5 text-xs transition-all cursor-pointer whitespace-nowrap py-0.5 px-1 rounded hover:bg-gray-100/60 dark:hover:bg-gray-800/60 ${
                          isHovered ? 'scale-[1.02] bg-gray-100/80 dark:bg-gray-800/80' : ''
                        }`}
                        title={
                          db.otherNames
                            ? `Others: ${db.otherNames}`
                            : `Click to view details for ${db.name}`
                        }
                      >
                        <span
                          className="w-2.5 h-2.5 rounded-sm shrink-0"
                          style={{ backgroundColor: color }}
                        />
                        <span className="font-medium text-gray-800 dark:text-gray-200">
                          {db.name}:
                        </span>
                        <span className="text-gray-500 dark:text-gray-100 font-mono text-[11px]">
                          {db.percentage.toFixed(1)}%
                        </span>
                        <span className="font-bold text-gray-900 dark:text-white font-mono text-[11px]">
                          {db.formattedSize}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Inventory Section (AgentTable theme) */}
      <div className="">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2.5">
          <div className="flex items-center gap-1.5">
            <Database className="w-3.5 h-3.5 text-orange-500" />
            <h2 className="text-xs font-bold text-gray-900 dark:text-gray-100 uppercase tracking-wider">
              {isMongo ? 'Collection Inventory' : 'Database Inventory'}
            </h2>
            <span className="text-xs text-gray-500 dark:text-gray-100 font-medium">
              ({summary.totalDatabases})
            </span>
          </div>

          {/* Search and Filter Controls */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-gray-100 dark:bg-gray-800/80 p-0.5 rounded-lg border border-gray-200 dark:border-gray-700">
              <button
                type="button"
                onClick={() => setCategoryFilter('all')}
                className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all duration-200 cursor-pointer ${categoryFilter === 'all'
                  ? 'bg-orange-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                All ({summary.totalDatabases})
              </button>
              <button
                type="button"
                onClick={() => setCategoryFilter('application')}
                className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all duration-200 cursor-pointer ${categoryFilter === 'application'
                  ? 'bg-orange-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                Application ({summary.appDatabases})
              </button>
              <button
                type="button"
                onClick={() => setCategoryFilter('system')}
                className={`px-3 py-1 rounded-md text-[11px] font-bold transition-all duration-200 cursor-pointer ${categoryFilter === 'system'
                  ? 'bg-orange-600 text-white shadow-sm'
                  : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                  }`}
              >
                System ({summary.sysDatabases})
              </button>
            </div>

            <div className="relative w-full sm:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-100 w-3.5 h-3.5 transition-colors duration-200" />
              <input
                type="text"
                placeholder={isMongo ? 'Search collections...' : 'Search databases...'}
                className="w-full bg-white dark:bg-gray-800/50 border border-gray-300 dark:border-gray-700 rounded-lg pl-8 pr-3 py-1.5 text-[11px] text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:border-orange-500 outline-none transition-colors duration-200 shadow-sm"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <DBExportModal
              endpoint="/db_monitor_dashboard/per_db/overview"
              columns={inventoryExportColumns}
              cluster_id={activeCluster}
              client_id={clientId}
              defaultSortBy={inventoryExportColumns.some(c => c.key === 'tables') ? 'tables' : inventoryExportColumns[0]?.key}
              defaultSortOrder="desc"
              buttonText="Export"
              title={isMongo ? 'Export Collections' : 'Export Databases'}
            />
          </div>
        </div>

        {/* Dynamic Table styled like AgentTable */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-t-xl overflow-hidden shadow-sm dark:shadow-2xl transition-colors duration-200">
          <div className="overflow-x-auto custom-scroll">
            <table className="w-full text-left text-xs border-collapse">
              <thead className="bg-gray-50 dark:bg-gray-800/80 sticky top-0 z-10 transition-colors duration-200">
                <tr className="border-b border-gray-200 dark:border-gray-700/50 transition-colors duration-200">
                  {/* Name Column */}
                  <th
                    onClick={() => handleSort('database')}
                    className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest cursor-pointer whitespace-nowrap select-none"
                  >
                    <div className="flex items-center gap-1.5">
                      <span>{isMongo ? 'COLLECTION / DB' : 'DATABASE'}</span>
                      {sortKey === 'database' && (
                        sortDirection === 'asc' ? <ChevronUp size={12} className="text-orange-500" /> : <ChevronDown size={12} className="text-orange-500" />
                      )}
                    </div>
                  </th>

                  {/* Category */}
                  <th
                    onClick={() => handleSort('category')}
                    className="px-3 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center cursor-pointer whitespace-nowrap select-none"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <span>CATEGORY</span>
                      {sortKey === 'category' && (
                        sortDirection === 'asc' ? <ChevronUp size={12} className="text-orange-500" /> : <ChevronDown size={12} className="text-orange-500" />
                      )}
                    </div>
                  </th>

                  {/* Total Size */}
                  <th
                    onClick={() => handleSort('total_size')}
                    className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right cursor-pointer whitespace-nowrap select-none"
                  >
                    <div className="flex items-center justify-end gap-1.5">
                      <span>TOTAL SIZE</span>
                      {sortKey === 'total_size' && (
                        sortDirection === 'asc' ? <ChevronUp size={12} className="text-orange-500" /> : <ChevronDown size={12} className="text-orange-500" />
                      )}
                    </div>
                  </th>

                  {/* ENGINE SPECIFIC HEADERS */}

                  {/* CLICKHOUSE */}
                  {detectedEngine === 'clickhouse' && (
                    <>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        COMPRESSED (DISK)
                      </th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        UNCOMPRESSED DATA
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        PARTS
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        MERGES
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        MUTATIONS
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        TABLES
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        VIEWS
                      </th>
                    </>
                  )}

                  {/* POSTGRESQL */}
                  {detectedEngine === 'postgresql' && (
                    <>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        CONNECTIONS
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        OPEN TXNS
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        CACHE HIT %
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        COMMITS/S
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        ROLLBACKS/S
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        DEAD TUPLES %
                      </th>
                    </>
                  )}

                  {/* MYSQL & MARIADB */}
                  {(detectedEngine === 'mysql' || detectedEngine === 'mariadb') && (
                    <>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        DATA SIZE
                      </th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        INDEX SIZE
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        TABLES
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        VIEWS
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        EST. ROWS
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        CONNECTIONS
                      </th>
                    </>
                  )}

                  {/* MONGODB */}
                  {detectedEngine === 'mongodb' && (
                    <>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        STORAGE SIZE
                      </th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        INDEX SIZE
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        DOCUMENTS
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        AVG OBJ SIZE
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        INDEXES
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center whitespace-nowrap">
                        CAPPED
                      </th>
                    </>
                  )}

                  {/* MSSQL */}
                  {detectedEngine === 'mssql' && (
                    <>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        DATA FILE USED
                      </th>
                      <th className="px-4 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        LOG FILE USED
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center whitespace-nowrap">
                        RECOVERY MODEL
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-right whitespace-nowrap">
                        TXNS/S
                      </th>
                      <th className="px-3.5 py-2.5 text-[10px] font-bold text-gray-700 dark:text-gray-100 uppercase tracking-widest text-center whitespace-nowrap">
                        STATE
                      </th>
                    </>
                  )}

                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700/30 transition-colors duration-200">
                {paginatedDatabases.length === 0 ? (
                  <tr>
                    <td
                      colSpan={11}
                      className="py-16 text-center text-gray-500 dark:text-gray-100 font-bold uppercase tracking-widest italic transition-colors duration-200"
                    >
                      {isMongo ? 'No collections found' : 'No databases found'}
                    </td>
                  </tr>
                ) : (
                  paginatedDatabases.map((db, idx) => {
                    const isSystem = db.category === 'System';
                    return (
                      <tr
                        key={db.database || idx}
                        onClick={() => setSelectedDb(db)}
                        className="cursor-pointer hover:bg-gray-100/70 dark:hover:bg-gray-800 transition-colors duration-150 group border-b border-gray-100 dark:border-gray-700/30"
                      >
                        {/* Name Cell */}
                        <td className="px-4 py-2.5 text-[11px] text-gray-900 dark:text-white font-semibold">
                          <div className="flex items-center gap-2">
                            <Database className="w-3.5 h-3.5 text-orange-500 shrink-0 group-hover:scale-110 transition-transform duration-150" />
                            <div className="min-w-0">
                              <span className="truncate block font-mono text-[11px] font-bold text-gray-900 dark:text-white group-hover:text-orange-600 dark:group-hover:text-orange-400 transition-colors">
                                {db.database}
                              </span>
                              {db.db_name && db.collection && (
                                <span className="text-[10px] text-gray-100 block truncate font-sans">
                                  DB: {db.db_name}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        {/* Category */}
                        <td className="px-3 py-2.5 text-center">
                          <span
                            className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${isSystem
                              ? 'bg-blue-500/10 text-blue-600 dark:text-blue-400 border border-blue-500/20'
                              : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                              }`}
                          >
                            {db.category}
                          </span>
                        </td>

                        {/* Total Size */}
                        <td className="px-4 py-2.5 text-[11px] text-right font-bold text-gray-900 dark:text-white font-mono whitespace-nowrap">
                          {formatSize(db.total_size)}
                        </td>

                        {/* CLICKHOUSE DATA CELLS */}
                        {detectedEngine === 'clickhouse' && (
                          <>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.compressed_size != null ? formatSize(db.compressed_size) : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.uncompressed_data_size != null ? formatSize(db.uncompressed_data_size) : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.active_parts != null ? db.active_parts : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.active_merges ?? 0}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.pending_mutations ?? 0}
                              {db.failed_mutations ? ` (${db.failed_mutations} failed)` : ''}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.tables ?? 0}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.views ?? 0}
                            </td>
                          </>
                        )}

                        {/* POSTGRESQL DATA CELLS */}
                        {detectedEngine === 'postgresql' && (
                          <>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.connections ?? 0}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.open_txns ?? 0}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right font-bold text-gray-900 dark:text-white font-mono whitespace-nowrap">
                              {db.cache_hit_pct != null ? `${Number(db.cache_hit_pct).toFixed(1)}%` : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.commits_per_s != null ? `${Number(db.commits_per_s).toFixed(2)}/s` : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.rollbacks_per_s != null ? `${Number(db.rollbacks_per_s).toFixed(2)}/s` : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.dead_tuples_pct != null ? `${Number(db.dead_tuples_pct).toFixed(1)}%` : '0%'}
                            </td>
                          </>
                        )}

                        {/* MYSQL & MARIADB DATA CELLS */}
                        {(detectedEngine === 'mysql' || detectedEngine === 'mariadb') && (
                          <>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.data_size_mb != null ? formatSize(db.data_size_mb) : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.index_size_mb != null ? formatSize(db.index_size_mb) : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.table_count ?? 0}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {(db.view_count ?? 0) + (db.system_view_count ?? 0)}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.row_estimate != null ? Number(db.row_estimate).toLocaleString() : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.active_connections ?? db.connections ?? 0}
                            </td>
                          </>
                        )}

                        {/* MONGODB DATA CELLS */}
                        {detectedEngine === 'mongodb' && (
                          <>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.storage_size_mb != null ? formatSize(db.storage_size_mb) : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.index_size_mb != null ? formatSize(db.index_size_mb) : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right font-bold text-gray-900 dark:text-white font-mono whitespace-nowrap">
                              {db.docs_count != null ? Number(db.docs_count).toLocaleString() : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.avg_obj_size_b != null ? `${Number(db.avg_obj_size_b).toLocaleString()} B` : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.index_count ?? '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-center whitespace-nowrap">
                              <span
                                className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase ${db.is_capped
                                  ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20'
                                  : 'text-gray-100'
                                  }`}
                              >
                                {db.is_capped ? 'Yes' : 'No'}
                              </span>
                            </td>
                          </>
                        )}

                        {/* MSSQL DATA CELLS */}
                        {detectedEngine === 'mssql' && (
                          <>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.data_file_used_mb != null ? formatSize(db.data_file_used_mb) : '—'}
                            </td>
                            <td className="px-4 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.log_file_used_mb != null ? formatSize(db.log_file_used_mb) : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-center text-gray-700 dark:text-gray-300 font-mono text-[10px] whitespace-nowrap">
                              {db.recovery_model || '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-[11px] text-right text-gray-700 dark:text-gray-300 font-mono whitespace-nowrap">
                              {db.transactions_per_s != null ? `${Number(db.transactions_per_s).toFixed(2)}/s` : '—'}
                            </td>
                            <td className="px-3.5 py-2.5 text-center whitespace-nowrap">
                              <span className="inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                                {db.status || 'ONLINE'}
                              </span>
                            </td>
                          </>
                        )}

                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Table Footer with Pagination styled like AgentTable */}
        <div className="bg-gray-50 dark:bg-gray-800/50 border rounded-b-xl border-gray-200 dark:border-gray-700 px-3 py-2 flex flex-col sm:flex-row items-center justify-between gap-3 transition-colors duration-200">
          <div className="flex items-center gap-4">
            <ItemsPerPageDropdown
              value={itemsPerPage}
              options={[10, 25, 50, 100]}
              onChange={(val: any) => {
                setItemsPerPage(Number(val));
                setCurrentPage(1);
              }}
            />
            <span className="text-[10px] text-gray-600 dark:text-gray-100 font-bold uppercase tracking-widest transition-colors duration-200">
              <span className="text-gray-900 dark:text-gray-200">{totalItems > 0 ? startIndex + 1 : 0}</span> -{' '}
              <span className="text-gray-900 dark:text-gray-200">{endIndex}</span> OF {totalItems}
            </span>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors duration-200"
                title="First Page"
              >
                <ChevronsLeft className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors duration-200"
                title="Previous Page"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              <div className="px-3 py-1 bg-orange-600 rounded-lg text-white text-[11px] font-bold mx-1.5 shadow-lg shadow-orange-900/20">
                {currentPage}
              </div>
              <button
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors duration-200"
                title="Next Page"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages || totalPages === 0}
                className="p-1.5 text-gray-600 hover:text-gray-900 dark:text-gray-100 dark:hover:text-white disabled:opacity-30 cursor-pointer disabled:cursor-not-allowed transition-colors duration-200"
                title="Last Page"
              >
                <ChevronsRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Database / Collection Size Distribution (Full Width) */}


      {/* Right Side Slide-Over Details Panel */}
      {selectedDb && (
        <DatabaseDetailsPanel
          db={selectedDb}
          detectedEngine={detectedEngine}
          onClose={() => setSelectedDb(null)}
          clusterId={activeCluster}
          clientId={clientId}
        />
      )}
    </div>
  );
};

export default OverviewView;
